import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import type { CSSObject } from 'styled-components';

// ============================================================
//     CONSTANTS
// ============================================================
/** How long the thumb stays visible after the last scroll/hover event, in ms. Matches FE2.0's `ScrollArea` default `scrollHideDelay`. */
const SCROLL_HIDE_DELAY_MS = 600;

/**
 * Track/thumb geometry, shared between the layout (`StyledScrollTrack`/
 * `StyledScrollThumb`) and the `computeThumb` math below so the thumb's travel
 * range always matches the track's visible bounds instead of the viewport's
 * full height.
 */
const SCROLLBAR_INSET = 2;
const SCROLLBAR_WIDTH = 8;
const SCROLLBAR_TOP = 2;
const SCROLLBAR_BOTTOM = 2;

// ============================================================
//     TYPES
// ============================================================
/**
 * The three scroll dimensions the thumb is derived from.
 *
 * Structurally satisfied by any DOM element, and by the payload
 * `react-virtualized`'s `Grid`/`List` `onScroll` prop reports — so a scroller we
 * don't own can feed `syncMetrics` directly.
 */
export interface ScrollMetrics {
  clientHeight: number;
  scrollHeight: number;
  scrollTop: number;
}

export interface ThumbGeometry {
  height: number;
  top: number;
}

// ============================================================
//     STYLES
// ============================================================
/**
 * Hides an element's native scrollbar without disabling its scrolling.
 *
 * Spread into whichever element actually scrolls — the viewport you own, or (via
 * a child selector) one owned by a third-party scroller — so the overlay thumb
 * below is the only visible affordance and no layout width is reserved.
 */
export const hideNativeScrollbar: CSSObject = {
  scrollbarWidth: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

/**
 * Positioning context for the absolutely-positioned track/thumb.
 *
 * Deliberately carries no layout of its own — extend it (`styled(OverlayScrollWrapper)`)
 * to add whatever flex/sizing the consumer's layout needs.
 */
export const OverlayScrollWrapper = styled.div({
  position: 'relative',
});

/**
 * Shadow a static element casts up over the scrolling content above it, marking
 * the boundary only while there is something to scroll — softer than a hairline
 * divider, and gone once the content fits.
 *
 * Pair it with `overflowShadowTransition` and a selector keyed off the
 * neighbouring scroll wrapper's `data-overflowing` attribute. Negate the
 * vertical offset for an element sitting above the scroller instead of below it.
 */
export const overflowShadow = '0px -6px 8px -4px oklch(0 0 0 / 0.1)';

/**
 * Transition scaffolding for `overflowShadow`. Spread it into the element that
 * casts the shadow, then set `boxShadow` in whichever selector signals overflow
 * — a sibling `[data-overflowing="true"]`, or the element's own attribute.
 */
export const overflowShadowTransition: CSSObject = {
  boxShadow: 'none',
  transitionProperty: 'box-shadow',
  transitionDuration: 'var(--cl-duration-300)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
};

const StyledScrollTrack = styled.div({
  position: 'absolute',
  top: SCROLLBAR_TOP,
  bottom: SCROLLBAR_BOTTOM,
  right: SCROLLBAR_INSET,
  width: SCROLLBAR_WIDTH,

  background: 'var(--cl-gray-300)',
  borderRadius: '4px',
  pointerEvents: 'none',

  opacity: 0,
  transitionProperty: 'opacity',
  transitionDuration: 'var(--cl-duration-200)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '&[data-visible="true"]': {
    opacity: 1,
  },
});

const StyledScrollThumb = styled.div({
  position: 'absolute',
  top: SCROLLBAR_TOP,
  right: SCROLLBAR_INSET,
  width: SCROLLBAR_WIDTH,

  background: 'var(--cl-gray-500)',
  borderRadius: '4px',
  pointerEvents: 'none',

  opacity: 0,
  transitionProperty: 'opacity',
  transitionDuration: 'var(--cl-duration-200)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '&[data-visible="true"]': {
    opacity: 1,
  },
});

// ============================================================
//     HELPERS
// ============================================================
/**
 * The thumb's size and offset for a given scroll position, or `null` when the
 * content fits and no scrollbar should be drawn at all.
 */
const computeThumb = ({
  clientHeight,
  scrollHeight,
  scrollTop,
}: ScrollMetrics): ThumbGeometry | null => {
  if (scrollHeight <= clientHeight) return null;

  // The thumb travels within the track's inset bounds, not the full viewport
  // height, so it never overshoots the track's rounded ends.
  const trackLength = clientHeight - SCROLLBAR_TOP - SCROLLBAR_BOTTOM;
  const height = (clientHeight / scrollHeight) * trackLength;
  const top = SCROLLBAR_TOP
    + ((scrollTop / (scrollHeight - clientHeight)) * (trackLength - height));

  return { height, top };
};

// ============================================================
//     HOOK
// ============================================================
/**
 * Tracks scroll position/content size for a custom overlay scrollbar thumb.
 *
 * Stand-in for the Frontend-Platform `ScrollArea` atom (no component-library
 * equivalent exists): unlike a native `overflow-y: auto` scrollbar, this never
 * reserves layout width, so it doesn't eat into narrow content — the collapsed
 * sidebar rail, or the fixed-width customer switcher popover — and cause labels
 * to truncate early.
 *
 * Two ways to feed it, depending on who owns the scrolling element:
 *
 * - **A viewport you own:** attach `viewportRef` and `handleScroll` to it. A
 *   `ResizeObserver` keeps the thumb correct as the viewport or its content
 *   resizes.
 * - **A scroller you don't own** (e.g. `react-virtualized`, which renders its
 *   own `overflow: auto` container): pass its reported metrics to `syncMetrics`.
 *   Spread `hideNativeScrollbar` onto that element and seed the initial geometry
 *   with `{ reveal: false }` so the thumb is ready before the first scroll.
 *
 * Either way, spread `wrapperProps` onto an `OverlayScrollWrapper` and render
 * `<OverlayScrollbar />` as a sibling of the scroller.
 */
export const useOverlayScrollbar = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [thumb, setThumb] = useState<ThumbGeometry | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const updateThumb = useCallback((metrics?: ScrollMetrics) => {
    const nextMetrics = metrics ?? viewportRef.current;
    /* v8 ignore next -- defensive: either metrics or an attached ref is always present by the time this runs */
    if (!nextMetrics) return;

    setThumb(computeThumb(nextMetrics));
  }, []);

  const revealThumb = useCallback(() => {
    setIsVisible(true);
    clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(
      () => setIsVisible(false),
      SCROLL_HIDE_DELAY_MS,
    );
  }, []);

  /** `onScroll` for a viewport attached to `viewportRef`. */
  const handleScroll = useCallback(() => {
    updateThumb();
    revealThumb();
  }, [updateThumb, revealThumb]);

  /**
   * Recompute from externally reported metrics, for a scroller we don't own.
   * Pass `{ reveal: false }` to seed geometry without flashing the thumb.
   */
  const syncMetrics = useCallback((
    metrics: ScrollMetrics,
    { reveal = true }: { reveal?: boolean } = {},
  ) => {
    updateThumb(metrics);
    if (reveal) revealThumb();
  }, [updateThumb, revealThumb]);

  // Owned separately from the ResizeObserver effect below, which bails early
  // when there is no viewport ref — `syncMetrics` arms this timer too.
  useEffect(() => () => clearTimeout(hideTimeoutRef.current), []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    updateThumb();

    const resizeObserver = new ResizeObserver(() => updateThumb());
    resizeObserver.observe(viewport);
    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild);
    }

    return () => resizeObserver.disconnect();
  }, [updateThumb]);

  return {
    handleScroll,
    isVisible,
    syncMetrics,
    thumb,
    viewportRef,
    wrapperProps: {
      'data-overflowing': !!thumb,
      // Cancel any hide armed by a preceding scroll — otherwise scrolling and
      // then settling the pointer over the wrapper hides the thumb mid-hover
      // when that timer fires.
      onMouseEnter: () => {
        clearTimeout(hideTimeoutRef.current);
        setIsVisible(true);
      },
      onMouseLeave: () => setIsVisible(false),
    },
  };
};

// ============================================================
//     COMPONENT
// ============================================================
interface OverlayScrollbarProps {
  isVisible: boolean;
  thumb: ThumbGeometry | null;
}

/**
 * The visible track and thumb, drawn over the scrolling content.
 *
 * Render as a sibling of the scroller, inside an `OverlayScrollWrapper`. Renders
 * nothing at all while the content fits (`thumb === null`).
 */
export const OverlayScrollbar = ({ isVisible, thumb }: OverlayScrollbarProps) => {
  if (!thumb) return null;

  return (
    <>
      <StyledScrollTrack data-visible={isVisible} />
      <StyledScrollThumb
        data-visible={isVisible}
        style={{ height: thumb.height, top: thumb.top }}
      />
    </>
  );
};
